import {
  BrandColorField,
  BusinessConfigData,
  BusinessConfigField,
  DisplayFontField,
} from "../../../interfaces/BusinessObject";

/**
 * `{ isEnabled, value? }` (TS) -> wire `{ is_enabled, value? }`. A PUT to a
 * field fully REPLACES that jsonb column — no server-side merge — so this
 * must be the field's complete intended value, not a diff. Callers that
 * disable a field but want to keep its stored value (e.g. slotCalendar, see
 * BusinessConfigData's doc comment) must pass that value through here
 * alongside isEnabled: false.
 */
function buildConfigFieldPayload(field: BusinessConfigField<unknown>): Record<string, unknown> {
  const wire: Record<string, unknown> = { is_enabled: field.isEnabled };
  if (field.value !== undefined) wire.value = field.value;
  return wire;
}

/** brand_color / display_font have no is_enabled — just `{ value?: string }`
 *  on the wire. Shared since both fields are structurally identical. */
function buildBareValuePayload(field: BrandColorField | DisplayFontField): Record<string, unknown> {
  const wire: Record<string, unknown> = {};
  if (field.value !== undefined) wire.value = field.value;
  return wire;
}

/**
 * Builds the PUT /config request body from a PARTIAL BusinessConfigData.
 * Only the keys actually present (and not `undefined`) are sent, and the
 * backend only writes the columns whose keys are present in the body — so
 * omitting a field here leaves that column untouched, while including it
 * (even as `{isEnabled: false}`) fully replaces it.
 */
export function buildUpdateBusinessConfigPayload(
  fields: Partial<BusinessConfigData>
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined) continue;
    payload[key] =
      key === "brandColor" || key === "displayFont"
        ? buildBareValuePayload(value as BrandColorField | DisplayFontField)
        : buildConfigFieldPayload(value as BusinessConfigField<unknown>);
  }
  return payload;
}
